import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MobileStickyBar from '@/components/feature/MobileStickyBar';
import useSEO from '@/hooks/useSEO';

const ContactPage = () => {
  useSEO({
    title: 'Contact Quinolux Glass & Mirrors | Las Vegas (702) 604-2093',
    description: 'Contact Quinolux Glass & Mirrors in Las Vegas. Call (702) 604-2093, email us, or fill out our contact form. Serving Las Vegas, Henderson, Summerlin & all of Clark County.',
    keywords: 'contact glass company Las Vegas, Quinolux phone number, glass installer Las Vegas contact',
    canonical: 'https://quinoluxglass.com/contact',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Quinolux Glass & Mirrors',
      description: 'Contact Quinolux Glass & Mirrors in Las Vegas. Call (702) 604-2093 or fill out our contact form.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/contact`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/` },
          { '@type': 'ListItem', position: 2, name: 'Contact', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/contact` },
        ],
      },
      mainEntity: {
        '@type': 'LocalBusiness',
        name: 'Quinolux Glass & Mirrors',
        telephone: '+17026042093',
        email: 'info@quinoluxglass.com',
        openingHours: ['Mo-Fr 08:00-18:00', 'Sa 09:00-16:00'],
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          addressCountry: 'US',
        },
      },
    },
  });

  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d7eoj2lfi84lst2k089g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch { setSubmitted(true); }
    finally { setSubmitting(false); }
  };

  return (
    <main>
      <Navbar />

      <section className="pt-32 pb-16 bg-[#F8F8F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4 block">Get In Touch</span>
          <h1 className="text-5xl md:text-6xl font-light text-black mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Contact Us</h1>
          <p className="text-gray-500 text-base max-w-xl mx-auto">We're here to help. Call, email, or fill out the form below and we'll get back to you within 2 hours.</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Info */}
            <div>
              <h2 className="text-3xl font-light text-black mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Let's Talk About Your Project</h2>
              <div className="space-y-6">
                <a href="tel:+17026042093" className="flex items-center gap-5 group cursor-pointer">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-black text-white flex-shrink-0 group-hover:bg-gray-800 transition-colors">
                    <i className="ri-phone-fill text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-0.5">Phone (Click to Call)</p>
                    <p className="text-black font-bold text-xl">(702) 604-2093</p>
                  </div>
                </a>
                <a href="mailto:info@quinoluxglass.com" className="flex items-center gap-5 group cursor-pointer">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#F8F8F6] text-black flex-shrink-0 group-hover:bg-gray-100 transition-colors">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-0.5">Email</p>
                    <p className="text-black font-medium">info@quinoluxglass.com</p>
                  </div>
                </a>
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#F8F8F6] text-black flex-shrink-0">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-0.5">Service Area</p>
                    <p className="text-black font-medium">Las Vegas Valley, NV</p>
                    <p className="text-gray-500 text-sm">Las Vegas · Henderson · Summerlin · North Las Vegas · Spring Valley · Paradise</p>
                  </div>
                </div>
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#F8F8F6] text-black flex-shrink-0">
                    <i className="ri-time-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-0.5">Hours</p>
                    <p className="text-black font-medium">Mon–Fri: 8am–6pm</p>
                    <p className="text-gray-500 text-sm">Saturday: 9am–4pm · Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-10 rounded-2xl overflow-hidden h-64 border border-gray-100">
                <iframe
                  title="Quinolux Glass Las Vegas"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d209550.5!2d-115.2!3d36.17!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80beb782a4f57dd1%3A0x3acf5b61c4d8a6e0!2sLas%20Vegas%2C%20NV!5e0!3m2!1sen!2sus!4v1700000000000"
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Form */}
            <div>
              <h2 className="text-3xl font-light text-black mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Send Us a Message</h2>
              {submitted ? (
                <div className="bg-[#F8F8F6] rounded-3xl p-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-black text-white mx-auto mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-black font-bold text-xl mb-2">Message Sent!</h3>
                  <p className="text-gray-500 text-sm">We'll get back to you within 2 hours during business hours.</p>
                </div>
              ) : (
                <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">Full Name *</label>
                      <input type="text" name="name" required value={formData.name} onChange={handleChange} placeholder="John Smith" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">Phone *</label>
                      <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} placeholder="(702) 000-0000" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Email *</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="john@example.com" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Message *</label>
                    <textarea name="message" required value={formData.message} onChange={handleChange} placeholder="Tell us about your project..." rows={6} maxLength={500} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors resize-none" />
                  </div>
                  <button type="submit" disabled={submitting} className="w-full bg-black text-white text-sm font-semibold py-4 rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60">
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </main>
  );
};

export default ContactPage;
